import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    DataList,
    DataFormFieldChangeParamType,
    DefaultMenuTypes,
    NotificationType,
    PageFilterSendType,
    PageGenericParamType,
    PageContainer,
    NotificationDialog,
    Loader,
    PageContainerActionParamType,
    SORT_ASC,
    SORT_DESC,
    getPathObject,
    AuthMenuType,
    useStateContext,
} from "@vegi.toenlioe/novitate-react-components";
import Karyawan from "../../../models/Karyawan";
import { useNavigate } from 'react-router-dom';
import {
    Add as AddIcon,
    FileDownload as FileDownloadIcon,
	FileUpload as FileUploadIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    Sort as SortIcon,
    SwapVert as SwapVertIcon,
} from '@mui/icons-material';
import { FlippedSortIcon } from "../../../styles";
import { MenuItems } from "../../../utils/Menu";

const groupName: string = "Umum";
const subgroupName: string = "Master";
const code: string = "GLOBAL-KARYAWAN";
const title: string = "Karyawan";
const endpoint = "karyawan";

function pathObject(): (PageContainerActionParamType | null) {
    return getPathObject(MenuItems, groupName, subgroupName, code);
};

function List() {
    const {authState, setAuthState, apiCaller} = useStateContext();
    const [error, setError] = useState<NotificationType | null>(null);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState<PageFilterSendType[]>([]);
    const [sort, setSort] = useState<PageGenericParamType | undefined>(undefined);
    const [shouldFilterData, setShouldFilterData] = useState(false);
    const [dataToEdit, setDataToEdit] = useState<Karyawan | null>(null);
    const [dataToDelete, setDataToDelete] = useState<Karyawan | null>(null);
    const [dataToView, setDataToView] = useState<Karyawan | null>(null);
    const [needReload, setNeedReload] = useState(false);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [pageCount, setPageCount] = useState<number>(1);
    const filtersRef = useRef<PageFilterSendType[]>([]);
    const navigate = useNavigate();
        
    const menuAuthState: AuthMenuType | undefined = useMemo(() => {
        return authState?.daftar_menu?.filter(m => m.kode_menu === code)[0];
    }, [authState]);

    useEffect(() => {
        setNeedReload(true);
    }, []);

    useEffect(() => {
        if (!needReload)
            return;

        setNeedReload(false);
    }, [needReload]);
    
    const handleFilterChanged = useCallback((filter: DataFormFieldChangeParamType<any>) => {
        const currentFilter = filtersRef.current.filter((f) => f.name === filter.fieldName)[0];

        if (!currentFilter)
        {
            if (!(filter.data as any)[filter.fieldName])
                return;

            filtersRef.current.push({
                name: filter.fieldName,
                value: (filter.data as any)[filter.fieldName],
            })
            return;
        }

        const currentFilterIndex = filtersRef.current.indexOf(currentFilter);

        if (!(filter.data as any)[filter.fieldName])
            filtersRef.current.splice(currentFilterIndex, 1);
        
        if ((filter.data as any)[filter.fieldName])
            filtersRef.current.splice(currentFilterIndex, 1, {name: filter.fieldName, value: (filter.data as any)[filter.fieldName]});
    }, [filtersRef.current]);

    const handleSortChanged = useCallback((key: string) => {
        const newSort: PageGenericParamType = sort ? JSON.parse(JSON.stringify(sort)) : ({name: key});

        if (!newSort.value)
        {
            newSort.value = SORT_ASC;
            setSort(newSort);
            setNeedReload(true);
            return;
        }

        if (newSort.value === SORT_ASC)
        {
            newSort.value = SORT_DESC;
            setSort(newSort);
            setNeedReload(true);
            return;
        }

        if (newSort.value === SORT_DESC)
        {
            setSort(undefined);
            setNeedReload(true);
            return;
        }
    }, [sort]);

	const handleAddClick = (pathObject: any) => {
		navigate(`${pathObject.mainPath}${pathObject.formPath}`, {state: {}});
	};

	const handleEditClick = useCallback((pathObject: any) => {
        if (!dataToEdit)
            return;

		navigate(`${pathObject.mainPath}${pathObject.formPath}`, {state: {...dataToEdit}});
    }, [navigate, dataToEdit]);

    useEffect(() => {
        if (!dataToEdit)
            return;

        handleEditClick(pathObject());
    }, [dataToEdit]);
    
    const handleViewClick = useCallback((pathObject: any) => {
        if (!dataToView)
            return;

        navigate(`${pathObject.mainPath}${pathObject.formPath}`, {state: {...dataToView, isViewMode: true}});
    }, [navigate, dataToView]);

    useEffect(() => {
        if (!dataToView)
            return;

        handleViewClick(pathObject());
    }, [dataToView]);

	const handleDeleteClick = useCallback(() => {
        if (!dataToDelete || !apiCaller)
            return;

        setLoading(true);

        apiCaller.delete(`${endpoint}/${dataToDelete.ucode}`)
            .then(() => {
                setDataToDelete(null);
                setLoading(false);
                setNeedReload(true);
            })
            .catch((error) => {
                console.error({error});
                setLoading(false);
            })
        ;
    }, [apiCaller, dataToDelete]);

    return (
		<>
        {
            loading && <Loader />
        }						
        <PageContainer
           
            navigate={navigate}
            menuItems={MenuItems}
			groupName={groupName}
			subgroupName={subgroupName}
			code={code}
			title={title}
			toolbars={[
				{
					id: "add",
					title: "Tambah Karyawan",
					icon: AddIcon,
					action: (pathObject) => handleAddClick(pathObject),
                    disabled: !(menuAuthState?.can_add ?? false),
				},
				{
					id: "exportExcel",
					title: "Download Excel",
					icon: FileDownloadIcon,
					action: () => {},
					color: "dark",
				},
				{
					id: "importExcel",
					title: "Upload",
					icon: FileUploadIcon,
					action: () => {},
					color: "dark",
				},					
			]}
            filter={{
                fields: [
                    {
                        fieldName: "kode",
                        fieldOperator: "contains",
                        fieldType: "string",
                        title: "Kode Karyawan",
                        inputType: "text",
                        gridSizeXl: 12,
                        gridSizeLg: 12,
                        gridSizeMd: 12,
                        onChanged: handleFilterChanged,
                        canSort: true,
                        titleHasCommands: true,
                        titleCommands: [
                            {
                                title: "Urut Kode Karyawan",
                                action: () => handleSortChanged("sort-kode"),
                                icon: sort?.name === "sort-kode" ? (sort?.value === SORT_DESC ? SortIcon : FlippedSortIcon) : SwapVertIcon,
                                id: "sort-kode",
                                variant: sort?.name === "sort-kode" ? "contained" : "outlined",
                            },
                        ]
                    },
                    {
                        fieldName: "nama",
                        fieldOperator: "contains",
                        fieldType: "string",
                        title: "Nama Karyawan",
                        inputType: "text",
                        gridSizeXl: 12,
                        gridSizeLg: 12,
                        gridSizeMd: 12,
                        onChanged: handleFilterChanged,
                        canSort: true,
                        titleHasCommands: true,
                        titleCommands: [
                            {
                                title: "Urut Nama Karyawan",
                                action: () => handleSortChanged("sort-nama"),
                                icon: sort?.name === "sort-nama" ? (sort?.value === SORT_DESC ? SortIcon : FlippedSortIcon) : SwapVertIcon,
                                id: "sort-nama",
                                variant: sort?.name === "sort-nama" ? "contained" : "outlined",
                            },
                        ]
                    },
                ],
                saveActionIcon: SearchIcon,
                saveActionCaption: "Cari",
                defaultFilters: filters,
            }}
            customFormCommands={[
                {
                    title: "Update Filter",
                    action: () => {
                        setFilters(filtersRef.current);
                        setShouldFilterData(true);
                        setNeedReload(true);
                    },
                    icon: SearchIcon,
                    color: "primary",
                },
                {
                    title: "Hapus Filter",
                    action: () => {
                        setFilters([]);
                        setShouldFilterData(false);
                        setNeedReload(true);
                    },
                    icon: DeleteIcon,
                }
            ]}
            onSetPageNumber={(value: number | undefined) => setPageNumber(value ?? 1)}
            pageCount={pageCount}
        >
            <DataList<Karyawan>
                dataType={Karyawan}
                keyField="ucode"
                setError={(errorState) => setError(errorState ?? null)}
                setLoading={(loadingState) => setLoading(loadingState ?? false)}
				actions={[
					{
						id: "Edit" as DefaultMenuTypes,
						title: "Ubah",
						action: (item) => setDataToEdit(item),
						icon: EditIcon,
						color: "primary",
                        disabled: () => !(menuAuthState?.can_edit ?? false),
					},
					{
						id: "View" as DefaultMenuTypes,
						title: "Lihat",
						action: (item) => setDataToView(item),
						icon: SearchIcon,
						color: "dark",
					},
					{
						id: "Delete" as DefaultMenuTypes,
						title: "Hapus",
						action: (item) => setDataToDelete(item),
						icon: DeleteIcon,
						color: "delete",
                        disabled: () => !(menuAuthState?.can_delete ?? false),
					},
				]}
				endpoint={endpoint}
                filters={shouldFilterData ? filters : []}
                sort={sort}
				titleColumnName="nama"
                details={[
                    {
                        title: "Kode",
                        fieldName: "kode",
                    },
                    {
                        title: "Email",
                        fieldName: "email",
                    },
                    {
                        title: "No. HP/Telp",
                        fieldName: "telp",
                    },
                ]}
				needReload={needReload}
                pageNumber={pageNumber}
                onSetPageCount={(value: number | undefined) => setPageCount(value ?? 1)}
            />
        </PageContainer>
        {
            dataToDelete ?
            <NotificationDialog
                message={`Hapus data "${dataToDelete.nama}"?`}
                onClose={() => setDataToDelete(null)}
                onConfirm={() => handleDeleteClick()}
                title="Konfirmasi"
                confirmButtonText="Ya"
                closeButtonText="Batal"
                warning
            />
            :
            null
        }
        {/* {
            error ?
            <NotificationDialog
                {...error}
                onClose={() => setError(null)}
            />
            :
            null
        } */}
		</>			
    );
};

export default List;